import * as THREE from 'three';
import type { ShipClass, RaceId, AsteroidSize } from '@viral-space/shared';

type MaterialType =
  | 'hull'
  | 'hull-wireframe'
  | 'asteroid'
  | 'asteroid-wireframe'
  | 'shield'
  | 'engine-glow'
  | 'laser';

const RACE_COLORS: Record<string, number> = {
  terran: 0x44aa66,
  wraith: 0x8844cc,
  insectoid: 0xaaaa22,
  mechanic: 0x5588cc,
};

const CLASS_SCALE: Record<string, number> = {
  fighter: 1.0,
  bomber: 1.4,
  interceptor: 0.8,
  cruiser: 2.2,
  frigate: 3.0,
};

const ASTEROID_SCALE: Record<string, number> = {
  small: 2,
  medium: 6,
  large: 14,
  huge: 30,
};

export class AssetLoader {
  private geometryCache = new Map<string, THREE.BufferGeometry>();
  private materialCache = new Map<string, THREE.Material>();

  getShipGeometry(race: RaceId, shipClass: ShipClass): THREE.BufferGeometry {
    const key = `ship:${race}:${shipClass}`;
    let geo = this.geometryCache.get(key);
    if (geo) return geo;

    geo = this.buildShipGeometry(race, shipClass);
    this.geometryCache.set(key, geo);
    return geo;
  }

  getAsteroidGeometry(size: AsteroidSize): THREE.BufferGeometry {
    const key = `asteroid:${size}`;
    let geo = this.geometryCache.get(key);
    if (geo) return geo;

    geo = this.buildAsteroidGeometry(size);
    this.geometryCache.set(key, geo);
    return geo;
  }

  getMaterial(type: MaterialType): THREE.Material {
    let mat = this.materialCache.get(type);
    if (mat) return mat;

    mat = this.buildMaterial(type);
    this.materialCache.set(type, mat);
    return mat;
  }

  /**
   * Creates a ship mesh group with solid hull + wireframe overlay.
   */
  createShipMesh(race: RaceId, shipClass: ShipClass): THREE.Group {
    const group = new THREE.Group();
    const geo = this.getShipGeometry(race, shipClass);

    const hull = new THREE.Mesh(geo, this.getHullMaterial(race));
    group.add(hull);

    const wire = new THREE.Mesh(geo, this.getHullWireMaterial(race));
    group.add(wire);

    return group;
  }

  /**
   * Creates an asteroid mesh group with solid body + wireframe overlay.
   */
  createAsteroidMesh(size: AsteroidSize): THREE.Group {
    const group = new THREE.Group();
    const geo = this.getAsteroidGeometry(size);

    const body = new THREE.Mesh(geo, this.getMaterial('asteroid'));
    group.add(body);

    const wire = new THREE.Mesh(geo, this.getMaterial('asteroid-wireframe'));
    group.add(wire);

    return group;
  }

  dispose(): void {
    this.geometryCache.forEach((g) => g.dispose());
    this.materialCache.forEach((m) => m.dispose());
    this.geometryCache.clear();
    this.materialCache.clear();
  }

  private getHullMaterial(race: RaceId): THREE.Material {
    const key = `hull:${race}`;
    let mat = this.materialCache.get(key);
    if (mat) return mat;

    const color = RACE_COLORS[String(race)] ?? 0x44aa66;
    mat = new THREE.MeshStandardMaterial({
      color,
      flatShading: true,
      roughness: 0.7,
      metalness: 0.3,
    });
    this.materialCache.set(key, mat);
    return mat;
  }

  private getHullWireMaterial(race: RaceId): THREE.Material {
    const key = `hull-wire:${race}`;
    let mat = this.materialCache.get(key);
    if (mat) return mat;

    const color = RACE_COLORS[String(race)] ?? 0x44aa66;
    mat = new THREE.MeshBasicMaterial({
      color,
      wireframe: true,
      transparent: true,
      opacity: 0.15,
      depthWrite: false,
    });
    this.materialCache.set(key, mat);
    return mat;
  }

  private buildMaterial(type: MaterialType): THREE.Material {
    switch (type) {
      case 'hull':
        return new THREE.MeshStandardMaterial({
          color: 0x44aa66,
          flatShading: true,
          roughness: 0.7,
          metalness: 0.3,
        });

      case 'hull-wireframe':
        return new THREE.MeshBasicMaterial({
          color: 0x66ff88,
          wireframe: true,
          transparent: true,
          opacity: 0.15,
          depthWrite: false,
        });

      case 'asteroid':
        return new THREE.MeshStandardMaterial({
          color: 0x554433,
          flatShading: true,
          roughness: 0.9,
          metalness: 0.1,
        });

      case 'asteroid-wireframe':
        return new THREE.MeshBasicMaterial({
          color: 0x776655,
          wireframe: true,
          transparent: true,
          opacity: 0.12,
          depthWrite: false,
        });

      case 'shield':
        return new THREE.MeshBasicMaterial({
          color: 0x44ddff,
          transparent: true,
          opacity: 0.15,
          side: THREE.DoubleSide,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        });

      case 'engine-glow':
        return new THREE.MeshBasicMaterial({
          color: 0x44ffaa,
          transparent: true,
          opacity: 0.8,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        });

      case 'laser':
        return new THREE.MeshBasicMaterial({
          color: 0x00ff44,
          transparent: true,
          opacity: 0.9,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        });

      default:
        return new THREE.MeshStandardMaterial({ color: 0xffffff, flatShading: true });
    }
  }

  private buildShipGeometry(race: RaceId, shipClass: ShipClass): THREE.BufferGeometry {
    const scale = CLASS_SCALE[String(shipClass)] ?? 1.0;
    const raceStr = String(race);
    let geo: THREE.BufferGeometry;

    if (raceStr === 'wraith' || raceStr === 'insectoid') {
      geo = this.buildOrganicShip(scale);
    } else {
      geo = this.buildAngularShip(scale);
    }

    geo.computeVertexNormals();
    return geo;
  }

  /**
   * Angular/geometric hull — Terran & Mechanic races.
   * Wedge nose, flat panels, engine nacelles.
   */
  private buildAngularShip(scale: number): THREE.BufferGeometry {
    const s = scale;
    const vertices = new Float32Array([
      // Nose (front point)
       0,     0,    -3*s,
      // Upper hull
      -0.8*s, 0.3*s, -1*s,
       0.8*s, 0.3*s, -1*s,
      -1.0*s, 0.2*s,  1.5*s,
       1.0*s, 0.2*s,  1.5*s,
      // Lower hull
      -0.8*s,-0.2*s, -1*s,
       0.8*s,-0.2*s, -1*s,
      -1.0*s,-0.1*s,  1.5*s,
       1.0*s,-0.1*s,  1.5*s,
      // Wing tips
      -2.0*s, 0.0,    1.0*s,
       2.0*s, 0.0,    1.0*s,
      // Engine block
      -0.6*s, 0.0,    2.0*s,
       0.6*s, 0.0,    2.0*s,
    ]);

    const indices = [
      // Top face
      0,2,1, 1,2,4, 1,4,3,
      // Bottom face
      0,5,6, 0,6,8, 6,8,7,
      // Left side
      0,1,5, 1,3,7, 1,7,5,
      // Right side
      0,6,2, 2,6,8, 2,8,4,
      // Left wing
      3,9,7,
      // Right wing
      4,8,10,
      // Rear
      3,4,12, 3,12,11, 7,8,12, 7,12,11,
    ];

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    geo.setIndex(indices);
    return geo;
  }

  /**
   * Organic/curved hull — Wraith & Insectoid races.
   * Based on an elongated icosahedron, squished and pointed.
   */
  private buildOrganicShip(scale: number): THREE.BufferGeometry {
    const base = new THREE.IcosahedronGeometry(1.2 * scale, 1);
    const pos = base.attributes.position;

    for (let i = 0; i < pos.count; i++) {
      let x = pos.getX(i);
      let y = pos.getY(i);
      let z = pos.getZ(i);

      // Elongate along Z
      z *= 2.5;
      // Flatten Y
      y *= 0.4;
      // Taper toward nose (negative Z)
      const taper = 1.0 - Math.max(0, -z / (3 * scale)) * 0.6;
      x *= taper;
      y *= taper;

      pos.setXYZ(i, x, y, z);
    }

    pos.needsUpdate = true;
    base.computeVertexNormals();
    return base;
  }

  private buildAsteroidGeometry(size: AsteroidSize): THREE.BufferGeometry {
    const radius = ASTEROID_SCALE[String(size)] ?? 6;
    const detail = radius > 10 ? 2 : 1;
    const geo = new THREE.IcosahedronGeometry(radius, detail);
    const pos = geo.attributes.position;

    // Perturb vertices for a rocky look
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const y = pos.getY(i);
      const z = pos.getZ(i);
      const len = Math.sqrt(x * x + y * y + z * z);
      const noise = 1.0 + (this.pseudoRandom(x * 13.7 + y * 27.1 + z * 5.3) - 0.5) * 0.4;
      const newLen = len * noise;
      const s = newLen / len;
      pos.setXYZ(i, x * s, y * s, z * s);
    }

    pos.needsUpdate = true;
    geo.computeVertexNormals();
    return geo;
  }

  private pseudoRandom(seed: number): number {
    const x = Math.sin(seed * 12.9898) * 43758.5453;
    return x - Math.floor(x);
  }
}
